import React, { FC, useState, useMemo } from 'react'
import classnames from 'classnames'
import { Collapse } from '@mui/material'
import { IoChevronUpSharp, IoChevronDownSharp } from 'react-icons/io5'
import { ProductFilterKeys } from '../../../types/product'
import { ProductTypeFilter } from './types'
import ProductItem from './FilterItem'
import { FilterObject } from '../../utils/constants/filters'
import { FilterValue } from '../../../types/storefront'

interface Props {
  value: ProductTypeFilter
  checkSelected: (type: string, label: string) => FilterValue | undefined
  onSelect: (type: ProductFilterKeys, value: FilterObject) => any
  onParentSelect?: (
    type: ProductFilterKeys,
    values: FilterObject[],
    isRemove: boolean
  ) => any
  className?: string
}

const ProductTypeList: FC<Props> = (props) => {
  const { onSelect, value, className, checkSelected, onParentSelect } = props
  const [open, setOpen] = useState(false)

  /** Selected children set */
  const inSelectedChildren = useMemo(
    () =>
      value.children?.filter(
        (i) => !checkSelected(ProductFilterKeys.type, i.label)
      ) || [],
    [value]
  )

  const isActive = useMemo(
    () =>
      value.children?.some((i) =>
        checkSelected(ProductFilterKeys.type, i.label)
      ) || checkSelected(ProductFilterKeys.type, value.label),
    [value]
  )

  const handleParentSelect = () => {
    if (!value.children) {
      const params = [
        {
          productType: value.label,
        },
      ]
      onParentSelect?.(
        ProductFilterKeys.type,
        [
          {
            count: 0,
            input: JSON.stringify(params),
            label: value.label,
            id: value.id,
            type: ProductFilterKeys.type,
          },
        ],
        !!checkSelected(ProductFilterKeys.type, value.label)
      )
      return
    }

    // If has unselected children
    // do select all
    if (!inSelectedChildren.length) {
      onParentSelect?.(
        ProductFilterKeys.type,
        value.children.map((i) => ({ ...i, type: ProductFilterKeys.type })),
        true
      )
    } else {
      onParentSelect?.(
        ProductFilterKeys.type,
        inSelectedChildren.map((i) => ({ ...i, type: ProductFilterKeys.type })),
        false
      )
    }
  }

  return (
    <div className={className}>
      <div
        className={classnames([
          'text-xs font-medium flex justify-between items-center cursor-pointer',
          {
            'py-3': !value.children,
          },
        ])}
      >
        <span
          className={classnames(['flex-1', { 'font-bold': isActive }])}
          onClick={handleParentSelect}
        >
          {value.label}
        </span>
        {value.children && (
          <div
            className="text-xs p-3 flex-shrink-0"
            onClick={() => setOpen(!open)}
          >
            {open ? <IoChevronUpSharp /> : <IoChevronDownSharp />}
          </div>
        )}
      </div>

      <Collapse in={open}>
        {value.children?.map((i) => (
          <ProductItem
            key={i.id}
            value={i}
            onSelect={onSelect}
            active={Boolean(checkSelected(ProductFilterKeys.price, i.label))}
            type={ProductFilterKeys.type}
          />
        ))}
      </Collapse>
    </div>
  )
}

export default ProductTypeList
